import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  AlertLevelEnum,
  DynamicExchangeRateTypeEnum,
  DynamicExchangeRateTypeStringEnum,
  PoolTypeEnum,
} from '@shared/Enums'
import { EthAddr, StringTypeEnum, dateToEthDate, toFieldMetaData } from '@shared/SchemaTypes'
import { toErc20Wei, toRoot365 } from '@shared/conversions'

import {
  PoolCreateFormData,
  PoolCreateFormDataDefaultObject,
  PoolCreateSchema,
  poolConfigSettingsFormat,
} from '@react/admin/PoolCreateForm'
import {
  PoolFlexCreateFormData,
  PoolFlexCreateFormDataDefaultObject,
  PoolFlexCreateSchema,
} from '@react/admin/PoolFlexCreateForm'
import { onPromise } from '@react/components/UiHelpers'
import { ethers } from 'ethers'
import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { SectionHeader } from '@react/components/common/SectionHeader'
import EditField from '@react/components/elements/EditField'
import { AssetImage } from '@react/components/icons/AssetImage'
import { alertActions } from '@react/redux/alertSlice'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { createPoolDynamic } from '@react/signedTxs/createPoolDynamicTx'
import { createPoolFlex } from '@react/signedTxs/createPoolFlexTx'
import { createPool } from '@react/signedTxs/createPoolTx'
import { yupEthAddr } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import * as yup from 'yup'
import { InferType } from 'yup'
import { LiquidityAsset } from '@shared/LiquidityAsset'

export const PoolDynamicCreateSchema = yup.object({
  tokenSymbol: yup
    .string()
    .required()
    .label('Token Symbol')
    .meta(toFieldMetaData({ propName: 'tokenSymbol' })),
  // PoolDTO
  addr: yupEthAddr
    .label('Pool Contract Address')
    .meta(toFieldMetaData({ propName: 'addr', stringType: StringTypeEnum.STABLE_COINS, offChain: true })),
  poolName: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'poolName' })),

  currency: yup
    .mixed<LiquidityAsset>()
    // .oneOf(Object.values(CurrencyEnum))
    // .default(CurrencyEnum.USD)
    .label('Currency')
    .meta(toFieldMetaData({ propName: 'currency', stringType: StringTypeEnum.STABLE_COIN_DEFINITION })),

  borrowerAddr: yupEthAddr.label('Borrower Manager Addr').meta(
    toFieldMetaData({
      propName: 'borrowerAddr',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  fiatGatewayAddress: yupEthAddr.label('Fiat gateway Addr').meta(
    toFieldMetaData({
      propName: 'fiatGatewayAddress',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  feeCollectorAddress: yupEthAddr.label('Fee Collector Addr').meta(
    toFieldMetaData({
      propName: 'feeCollectorAddress',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
})

export interface PoolDynamicCreateFormData extends InferType<typeof PoolDynamicCreateSchema> {
  tokenSymbol: string
  maxCapacity: number

  addr: EthAddr
  borrowerAddr: EthAddr
  fiatGatewayAddress: EthAddr
  feeCollectorAddress: EthAddr
  closeOfDepositTimeHour: number
  closeOfWithdrawTimeHour: number
  transferOutDays: number
}

interface Props {
  vaultType: PoolTypeEnum
  vaultParams: any
  handleChange: (event: SelectChangeEvent<string>) => void
  handleComplete: (addr: EthAddr) => void
}

export const VaultParamsStep: React.FC<Props> = ({ vaultType, handleComplete }) => {
  const {
    webSocket,
    userConnection: { signer },
    chainConfiguration: { perimeterAddresses, chainConfigurationName },
  } = useOtChainState()

  const initialValues =
    vaultType === PoolTypeEnum.Term || !perimeterAddresses
      ? PoolCreateFormDataDefaultObject()
      : PoolFlexCreateFormDataDefaultObject(perimeterAddresses)

  const SUPPORTED_ASSETS: LiquidityAsset[] = perimeterAddresses?.liquidityAssets ?? []

  const [selectedAsset, setSelectedAsset] = useState(SUPPORTED_ASSETS[0])
  const [selectedErt, setSelectedErt] = useState(DynamicExchangeRateTypeStringEnum.Undefined)

  const handleAssetChange = (event: SelectChangeEvent) => {
    setSelectedAsset(
      SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === event.target.value) ?? SUPPORTED_ASSETS[0]
    )
  }
  const handleErtChange = (event: SelectChangeEvent) => {
    setSelectedErt(event.target.value as DynamicExchangeRateTypeStringEnum)
  }

  const dispatch = useOtDispatch()
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PoolCreateFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(
      vaultType === PoolTypeEnum.Term
        ? PoolCreateSchema
        : vaultType === PoolTypeEnum.FlexibleRate
          ? PoolFlexCreateSchema
          : PoolDynamicCreateSchema
    ),
  })

  const onErrors = (errors: any) => {
    logOT.info('ERRORS: ', errors)
  }

  const onSubmit: SubmitHandler<PoolCreateFormData> = async (
    poolFormData: PoolCreateFormData | PoolFlexCreateFormData | PoolDynamicCreateFormData
  ): Promise<void> => {
    setLoading(true)

    console.log('poolFormData on submit vaultType', vaultType)
    if (signer) {
      if (perimeterAddresses && chainConfigurationName && vaultType === PoolTypeEnum.Term) {
        const poolFixedTermFormData = poolFormData as PoolCreateFormData
        const poolConfigSettings = poolConfigSettingsFormat(poolFixedTermFormData)
        createPool(
          perimeterAddresses,
          webSocket,
          signer as ethers.Signer,
          poolConfigSettings,
          poolFormData.poolName,
          poolFormData.tokenSymbol,
          selectedAsset.addr,
          chainConfigurationName
        ).then((createPoolReturn) => {
          setLoading(false)
          const poolAddr = createPoolReturn.poolAddr
          handleComplete(poolAddr)
        })
      } else if (vaultType === PoolTypeEnum.FlexibleRate) {
        const poolFlexFormData = poolFormData as PoolFlexCreateFormData
        const poolConfigSettingsStruct = {
          maxCapacity: toErc20Wei(poolFlexFormData.maxCapacity),
          borrowerManagerAddr: poolFlexFormData.borrowerAddr,
          borrowerWalletAddr: poolFlexFormData.borrowerWalletAddr,

          closeOfDepositTime: (poolFlexFormData.closeOfDepositTimeHour * 3600).toString(),
          closeOfWithdrawTime: (poolFlexFormData.closeOfWithdrawTimeHour * 3600).toString(),

          originationFee: Math.round(poolFlexFormData.originationFee).toString(),
          dailyOriginationFeeRate: toRoot365(Math.round(poolFlexFormData.originationFee)).toString(),
          transferInDays: poolFlexFormData.transferInDays.toString(),
          transferOutDays: poolFlexFormData.transferOutDays.toString(),
        }
        const poolConfigSettings = Object.values(poolConfigSettingsStruct) as string[]
        if (signer && perimeterAddresses && chainConfigurationName) {
          createPoolFlex(
            perimeterAddresses,
            webSocket,
            signer,
            poolConfigSettings,
            poolFormData.poolName,
            poolFormData.tokenSymbol,
            selectedAsset.addr,
            chainConfigurationName
          )
            .then((createPoolReturn) => {
              setLoading(false)
              const poolAddr = createPoolReturn.poolAddr
              handleComplete(poolAddr)
            })
            .catch((_err: any) => {
              dispatch(
                alertActions.addAlert({
                  title: 'Error creating flex vault.',
                  sourceObjectAddr: '',
                  description: 'Please check the input values and try again.',
                  alertLevel: AlertLevelEnum.ERROR,
                  createdAt: dateToEthDate(new Date()),
                  dueDateTime: 0,
                })
              )
              setLoading(false)
            })
        }
      } else if (vaultType === PoolTypeEnum.Dynamic) {
        const poolDynamicFormData = poolFormData as PoolDynamicCreateFormData

        logOT.debug('selectedErt', selectedErt)

        const exchangeRateType: DynamicExchangeRateTypeEnum =
          selectedErt == DynamicExchangeRateTypeStringEnum.Undefined
            ? DynamicExchangeRateTypeEnum.Undefined
            : selectedErt == DynamicExchangeRateTypeStringEnum.Dynamic
              ? DynamicExchangeRateTypeEnum.Dynamic
              : selectedErt == DynamicExchangeRateTypeStringEnum.Linear
                ? DynamicExchangeRateTypeEnum.Linear
                : selectedErt == DynamicExchangeRateTypeStringEnum.Compounding
                  ? DynamicExchangeRateTypeEnum.Compounding
                  : selectedErt == DynamicExchangeRateTypeStringEnum.Term
                    ? DynamicExchangeRateTypeEnum.Term
                    : DynamicExchangeRateTypeEnum.Undefined

        const poolConfigSettings = [
          poolDynamicFormData.borrowerAddr,
          poolDynamicFormData.fiatGatewayAddress,
          poolDynamicFormData.feeCollectorAddress,
          (poolDynamicFormData.closeOfDepositTimeHour * 3600).toString(),
          (poolDynamicFormData.closeOfWithdrawTimeHour * 3600).toString(),
          poolDynamicFormData.transferOutDays.toString(),
          exchangeRateType.toString(),
        ]
        if (signer && perimeterAddresses && chainConfigurationName) {
          createPoolDynamic(
            perimeterAddresses,
            webSocket,
            signer,
            poolConfigSettings,
            poolFormData.poolName,
            poolFormData.tokenSymbol,
            selectedAsset.addr,
            chainConfigurationName
          )
            .then((createPoolReturn) => {
              setLoading(false)
              const poolAddr = createPoolReturn.poolAddr
              handleComplete(poolAddr)
            })
            .catch((_err: any) => {
              dispatch(
                alertActions.addAlert({
                  title: 'Error creating Dynamic vault.',
                  sourceObjectAddr: '',
                  description: 'Please check the input values and try again.',
                  alertLevel: AlertLevelEnum.ERROR,
                  createdAt: dateToEthDate(new Date()),
                  dueDateTime: 0,
                })
              )
              setLoading(false)
            })
        }
      } else {
        alert(`Unknown pool type-d ${vaultType}`)
      }
    }
  }

  return (
    <Box>
      <form onSubmit={onPromise(handleSubmit(onSubmit, onErrors))}>
        <SectionHeader>Vault Details</SectionHeader>
        <Grid container spacing={1} sx={{ marginTop: 4 }}>
          <Grid item xs={12} sm={12}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolCreateSchema.describe().fields.poolName}
            ></EditField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl>
              <InputLabel id="asset-label">Asset</InputLabel>
              <Select
                labelId="asset-label"
                id="asset-select"
                input={<OutlinedInput sx={{ marginTop: 1 }} id="asset-select" label="Asset" />}
                value={selectedAsset.symbol}
                renderValue={(assetSymbol: string) => {
                  const asset =
                    SUPPORTED_ASSETS.find((asset: LiquidityAsset) => asset.symbol === assetSymbol) ??
                    SUPPORTED_ASSETS[0]
                  return (
                    <div
                      style={{
                        flexDirection: 'row',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        minWidth: 200,
                      }}
                    >
                      <div style={{ marginTop: '4px' }}>
                        <AssetImage asset={asset.symbol} />
                      </div>
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {asset.symbol}
                      </Typography>
                    </div>
                  )
                }}
                onChange={handleAssetChange}
              >
                {SUPPORTED_ASSETS.map((asset: LiquidityAsset) => (
                  <MenuItem
                    value={asset.symbol}
                    key={`asset-option-${asset.symbol}`}
                    style={{
                      paddingLeft: 12,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ marginTop: '4px' }}>
                      <AssetImage asset={asset.symbol} />
                    </div>
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      {asset.symbol}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={4}>
              <FormControl>
                <InputLabel id="ert-label">Exchange Rate Type</InputLabel>
                <Select
                  labelId="exchange-rate-type-label"
                  id="ert-select"
                  input={<OutlinedInput sx={{ marginTop: 1 }} id="ert-select" label="Asset" />}
                  value={selectedErt}
                  renderValue={(ert: string) => {
                    return (
                      <div
                        style={{
                          flexDirection: 'row',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          minWidth: 200,
                        }}
                      >
                        <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                          {ert}
                        </Typography>
                      </div>
                    )
                  }}
                  onChange={handleErtChange}
                >
                  {Object.values(DynamicExchangeRateTypeStringEnum).map((ert: string) => (
                    <MenuItem
                      value={ert}
                      key={`ert-option-${ert}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {ert}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={4} sx={{ marginLeft: 20 }}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolCreateSchema.describe().fields.tokenSymbol}
            ></EditField>
          </Grid>
        </Grid>

        <SectionHeader style={{ marginTop: 20 }}>Borrower Wallet Addresses</SectionHeader>
        <Grid container spacing={1} sx={{ marginTop: 4 }}>
          <Grid item xs={12} sm={6}>
            <EditField
              errors={errors}
              register={register}
              initialValues={initialValues}
              fieldSchema={PoolCreateSchema.describe().fields.borrowerAddr}
            ></EditField>
          </Grid>
          {vaultType !== PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolCreateSchema.describe().fields.borrowerWalletAddr}
              ></EditField>
            </Grid>
          )}
          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolCreateSchema.describe().fields.fiatGatewayAddress}
              ></EditField>
            </Grid>
          )}
          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolCreateSchema.describe().fields.feeCollectorAddress}
              ></EditField>
            </Grid>
          )}

          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfDepositTimeHour}
              ></EditField>
            </Grid>
          )}
          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfWithdrawTimeHour}
              ></EditField>
            </Grid>
          )}
          {vaultType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <EditField
                errors={errors}
                register={register}
                initialValues={initialValues}
                fieldSchema={PoolFlexCreateSchema.describe().fields.transferOutDays}
              ></EditField>
            </Grid>
          )}
        </Grid>

        {vaultType === PoolTypeEnum.FlexibleRate && (
          <>
            <SectionHeader style={{ marginTop: 20 }}>Cut-Off & Processing Times</SectionHeader>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfDepositTimeHour}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.closeOfWithdrawTimeHour}
                ></EditField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.originationFee}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.transferInDays}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.transferOutDays}
                ></EditField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <EditField
                  errors={errors}
                  register={register}
                  initialValues={initialValues}
                  currency={selectedAsset.symbol}
                  fieldSchema={PoolFlexCreateSchema.describe().fields.maxCapacity}
                ></EditField>
              </Grid>
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <Tooltip title="This will create the new vault. Additional setup required afterwards.">
            <Button
              disabled={loading}
              type="submit"
              variant="contained"
              sx={{ marginBottom: 4 }}
              onClick={() => handleSubmit(onSubmit, onErrors)}
            >
              {loading && <CircularProgress />}
              Create Vault
            </Button>
          </Tooltip>
        </Grid>
      </form>
    </Box>
  )
}
